import React from "react";
import { Button } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotifications } from "@toolpad/core/useNotifications";
import { useAuth } from "../../../context/auth";
import {
  Process,
  Service,
  CreateProcessRequest,
  UpdateProcessRequest,
  updateProcess,
  createCompleteProcess,
} from "../../../api/processes";
import { ProcessSection } from "../../../api/process-sections";

interface ProcessFormSubmitProps {
  process?: Process | null;
  services: Service[];
  processSections: ProcessSection[];
  followUpGroups: any[];
  formData?: any;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const ProcessFormSubmit: React.FC<ProcessFormSubmitProps> = ({
  process,
  services,
  processSections,
  followUpGroups,
  formData: externalFormData,
  onSuccess,
  onError,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  // Mutations
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateProcessRequest }) =>
      updateProcess(id, data, user?.token || ""),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["processes"] });
      notifications.show(response.message || "فرایند با موفقیت بروزرسانی شد", {
        severity: "success",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      notifications.show(error.message || "خطا در بروزرسانی فرایند", {
        severity: "error",
      });
      onError?.(error);
    },
  });

  const createCompleteMutation = useMutation({
    mutationFn: (data: CreateProcessRequest) =>
      createCompleteProcess(data, user?.token || ""),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["processes"] });
      notifications.show(response.message || "فرایند با موفقیت ایجاد شد", {
        severity: "success",
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      notifications.show(error.message || "خطا در ایجاد فرایند", {
        severity: "error",
      });
      onError?.(error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!externalFormData) {
      notifications.show("فرم در حال بارگذاری است، لطفاً صبر کنید", {
        severity: "warning",
      });
      return;
    }

    if (externalFormData.validateForm()) {
      if (process) {
        // For editing, use the simple update
        updateMutation.mutate({
          id: process.id,
          data: {
            title: externalFormData.formData.title,
            serviceRef: externalFormData.formData.serviceRef,
          },
        });
      } else {
        // Transform the data for the complete process creation
        const transformedData = {
          title: externalFormData.formData.title,
          serviceRef: externalFormData.formData.serviceRef,
          selectedSections: externalFormData.selectedSections,
          stages: externalFormData.stages.map((stage: any) => ({
            stageNumber: stage.stageNumber,
            title: stage.title,
            sectionRef: stage.sectionId,
            isPublic: stage.isPublic,
            followUpGroupIdsJson: stage.followUpGroupIdsJson,
          })),
          reasonSolutions: externalFormData.reasonSolutions.map((rs: any) => ({
            row: rs.row,
            isPrimary: rs.isPrimary,
            reason: rs.reason,
            solution: rs.solution,
            previousReasonIdsJson: rs.previousReasonIdsJson,
            stageRef: rs.stageRef,
          })),
        };

        createCompleteMutation.mutate(transformedData);
      }
    }
  };

  const loading = updateMutation.isPending || createCompleteMutation.isPending;
  const isEditing = !!process;

  return (
    <Button
      type="submit"
      variant="contained"
      color="primary"
      disabled={loading}
      onClick={handleSubmit}
      sx={{ minWidth: 120 }}
    >
      {loading ? "در حال ذخیره..." : isEditing ? "به‌روزرسانی" : "ایجاد"}
    </Button>
  );
};
